import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { OAuthService } from '@/services/OAuthService'
import { authorizationKeys } from '@/hooks/queryKeys'
import type { PendingAuthorizationDto, AuthorizationDecisionDto } from '@lib/dto/oauth.dto'
import type { ApproveAuthorizationInput } from '@lib/input/oauth.input'

/**
 * The authorization waiting for an answer.
 *
 * Not retried and never refetched in the background. It is a single-use request
 * that a moment ago did not exist and in ten minutes will not again, so a retry
 * would only be a second way to fail and a refetch could replace what somebody
 * is halfway through reading.
 */
export function useAuthorizationRequest(
  requestId: string,
): UseQueryResult<PendingAuthorizationDto> {
  return useQuery({
    queryKey: authorizationKeys.detail(requestId),
    queryFn: () => OAuthService.pending(requestId),
    enabled: requestId !== '',
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  })
}

/** Approve what was ticked, or decline outright. Both answer with where to go next. */
export function useAnswerAuthorization(
  requestId: string,
): UseMutationResult<
  AuthorizationDecisionDto,
  Error,
  { answer: 'approve'; input: ApproveAuthorizationInput } | { answer: 'deny' }
> {
  return useMutation({
    mutationFn: (decision) =>
      decision.answer === 'deny'
        ? OAuthService.deny(requestId)
        : OAuthService.approve(requestId, decision.input),
  })
}
